import { useEffect, useRef, useState } from 'react';

import blueCheckIcon from '@perpay-web/components/assets/images/blueCheck.svg';
import redXIcon from '@perpay-web/components/assets/images/redX.svg';
import yellowExclamation from '@perpay-web/components/assets/images/yellowExclamation.svg';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from './Toast.scss';

const ICONS = {
    success: blueCheckIcon,
    error: redXIcon,
    warning: yellowExclamation,
};

const getIconUrl = (type) => ICONS[type] || redXIcon;

/**
 * Single toast component.
 *
 * @param {{
 *   toast: import("./useToastContext").Toast,
 *   onRemove: (id: string) => void,
 * }} props
 * @returns {JSX.Element}
 */
const Toast = ({ toast, onRemove }) => {
    const { id, type, message, duration } = toast;

    const entranceTimerRef = useRef(null);
    const exitTimerRef = useRef(null);

    const [isMounted, setIsMounted] = useState(false);
    const [isExiting, setIsExiting] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Auto-dismiss with exit animation
    useEffect(() => {
        entranceTimerRef.current = setTimeout(() => {
            setIsExiting(true);
            // Remove after the exit animation duration (matches CSS: 600ms).
            exitTimerRef.current = setTimeout(() => onRemove(id), 600);
        }, duration);

        return () => {
            clearTimeout(entranceTimerRef.current);
            clearTimeout(exitTimerRef.current);
        };
    }, [duration, id, onRemove]);

    const iconUrl = getIconUrl(type);

    return (
        <div
            className={getClassName(
                styles.toast,
                isMounted && !isExiting && styles['toast--open'],
                isExiting && styles['toast--exit'],
            )}
        >
            <img src={iconUrl} alt={type} className={styles['toast-icon']} />
            <span className={styles['toast-message']}>{message}</span>
        </div>
    );
};

export default Toast;
